import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AlertService } from './../pos/services/alert/alert.service';
import { HotToastService } from '@ngneat/hot-toast';
import { Subscription, merge } from 'rxjs';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CheckTokenResponse } from '../pos/models/forgot-pin.model';

@Component({
  selector: 'app-pin-recovery',
  templateUrl: './pin-recovery.component.html',
  styleUrls: ['./pin-recovery.component.scss'],
})
export class PinRecoveryComponent implements OnInit, OnDestroy {
  pinForm: FormGroup;
  subscriptions: Subscription[] = [];
  token = '';
  currentUser: any;
  accessToken: '';
  recoveryStep = 1;
  storePin = '';

  constructor(
    private fb: FormBuilder,
    private toastService: HotToastService,
    private alertService: AlertService,
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    private router: Router,
    private authService: AuthService
  ) {
    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe((params: Params) => {
        if (params.token) {
          this.token = params.token;
        }
      })
    );
  }

  ngOnInit(): void {
    this.verifyToken();
    this.initForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  verifyToken(): void {
    this.http.post<CheckTokenResponse>(`${environment.apiUrl}/sessions/verify`, { token: this.token }).subscribe(
      (result) => {
        if (result.success) {
          this.currentUser = result.data.user;
          this.accessToken = result.data.access_token;
        } else {
          this.alertService.openDefaultDialog({ title: 'เกิดข้อผิดพลาด|คุณไม่สามารถเข้าใช้งานในส่วนนี้ได้' });
          this.router.navigate(['/']);
        }
      },
      (error) => {
        this.alertService.openDefaultDialog({ title: 'เกิดข้อผิดพลาด|คุณไม่สามารถเข้าใช้งานในส่วนนี้ได้' });
        this.router.navigate(['/']);
      }
    );
  }

  initForm(): void {
    this.pinForm = this.fb.group({
      position_1: ['', [Validators.required]],
      position_2: ['', [Validators.required]],
      position_3: ['', [Validators.required]],
      position_4: ['', [Validators.required]],
    });

    this.subscriptions.push(
      this.pinForm.controls.position_1.valueChanges.subscribe((change) => {
        const lastLetter = change.split('').at(-1);
        if (lastLetter) {
          this.pinForm.patchValue(
            {
              position_1: lastLetter,
            },
            { emitEvent: false }
          );
          const next = document.getElementById('position_2');
          if (next) {
            next.focus();
          }
        }
      })
    );
    this.subscriptions.push(
      this.pinForm.controls.position_2.valueChanges.subscribe((change) => {
        const lastLetter = change.split('').at(-1);
        if (lastLetter) {
          this.pinForm.patchValue(
            {
              position_2: lastLetter,
            },
            { emitEvent: false }
          );
          const next = document.getElementById('position_3');
          if (next) {
            next.focus();
          }
        }
      })
    );
    this.subscriptions.push(
      this.pinForm.controls.position_3.valueChanges.subscribe((change) => {
        const lastLetter = change.split('').at(-1);
        if (lastLetter) {
          this.pinForm.patchValue(
            {
              position_3: lastLetter,
            },
            { emitEvent: false }
          );
          const next = document.getElementById('position_4');
          if (next) {
            next.focus();
          }
        }
      })
    );
    this.subscriptions.push(
      this.pinForm.controls.position_4.valueChanges.subscribe((change) => {
        const lastLetter = change.split('').at(-1);
        if (lastLetter) {
          this.pinForm.patchValue(
            {
              position_4: lastLetter,
            },
            { emitEvent: false }
          );
        }
      })
    );
  }

  resetStorePin(): void {
    this.storePin = '';
    this.recoveryStep = 1;
    this.pinForm.patchValue({
      position_1: '',
      position_2: '',
      position_3: '',
      position_4: '',
    });
    const next = document.getElementById('position_1');
    if (next) {
      next.focus();
    }
  }

  async onSubmit(): Promise<void> {
    if (this.pinForm.invalid) {
      this.toastService.error('กรุณากรอก PIN ให้ครบ');
      return;
    }
    const extractLetter = [];
    for (const key in this.pinForm.value) {
      if (Object.prototype.hasOwnProperty.call(this.pinForm.value, key)) {
        const element = this.pinForm.value[key];
        extractLetter.push(element);
      }
    }
    const mergeLetter = extractLetter.join('');

    if (this.recoveryStep === 1) {
      this.storePin = mergeLetter;
      this.pinForm.patchValue({
        position_1: '',
        position_2: '',
        position_3: '',
        position_4: '',
      });
      this.recoveryStep = 2;
      const next = document.getElementById('position_1');
      if (next) {
        next.focus();
      }
      return;
    }

    if (this.storePin !== mergeLetter) {
      this.pinForm.patchValue({
        position_1: '',
        position_2: '',
        position_3: '',
        position_4: '',
      });
      this.toastService.error('กรุณากรอก PIN ให้ตรงกัน');
      const next = document.getElementById('position_1');
      if (next) {
        next.focus();
      }
      return;
    }

    const confirm = await this.alertService.openConfirmDialog(`คุณตรวจสอบความถูกต้อง|เรียบร้อยแล้วใช่หรือไม่?`);

    if (!confirm) return;

    const payload = {
      pin: mergeLetter,
      user_id: this.currentUser.id,
    };

    let headers = new HttpHeaders();
    headers = headers.append('Authorization', this.accessToken);

    this.http.post(`${environment.apiUrl}/users/update_pin`, payload, { headers }).subscribe(
      (result) => {
        this.alertService.openDefaultDialog({
          title: 'กู้คืนรหัส PIN เสร็จสิ้น|กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
          icon: 'check_circle',
        });
        this.router.navigate(['/']);
      },
      (error) => {
        this.alertService.openDefaultDialog({ title: 'เกิดข้อผิดพลาด|กรุณาลองใหม่อีกครั้ง' });
        this.router.navigate(['/']);
      }
    );
  }
}
